import { Timestamp } from "firebase/firestore";

export const PRODUCT_VERSIONS_COLLECTION = "productVersions";

export type ProductVersion = {
  id: string;
  nameFr: string;
  nameEn: string;
  activityField: string;
  sector: string;
  operatingMode: string;
  idsev: string;
  version: string;
  idversi: string;
  idpartn: string;
  codcertif: string;
  apprlinit: string;
  jsonCertificate: string;
  jsonDocument: string;
  jsonTransaction: string;
  jsonUser: string;
  parentVersion: string;
  parentIdversi: string;
  certificationStatus: "certified" | "pending";
  certifiedAt?: Timestamp;
  isDefault: boolean;
  isActive: boolean;
  locked: boolean;
  lockConfirmed?: boolean;
  lockedAt?: Timestamp;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
};

export const DEFAULT_PRODUCT_VERSION_FIELDS = {
  nameFr: "NuagePOS Eagle",
  nameEn: "NuagePOS Eagle",
  activityField: "Mandatory billing",
  sector: "Restaurant",
  operatingMode: "Server",
  idsev: "0000000000003B21",
  version: "6.6.1",
  idversi: "0000000000004891",
  idpartn: "0000000000001D71",
  codcertif: "FOB201999999",
  apprlinit: "SEV",
  jsonCertificate: "02.00",
  jsonDocument: "02.00",
  jsonTransaction: "02.00",
  jsonUser: "02.00",
  parentVersion: "6.6.1",
  parentIdversi: "0000000000004891",
  certificationStatus: "pending" as const,
  isDefault: true,
  isActive: true,
  locked: false,
  lockConfirmed: false,
};

export function productVersionLabel(version?: { nameEn?: string; version?: string } | null): string {
  if (!version) return "NuagePOS Eagle 6.6.1";
  const name = version.nameEn || version.version || "Product";
  return version.version ? `${name} ${version.version}` : name;
}

export function emptyProductVersion(): Omit<ProductVersion, "id"> {
  return {
    ...DEFAULT_PRODUCT_VERSION_FIELDS,
    isDefault: false,
    isActive: true,
    locked: false,
    lockConfirmed: false,
    certificationStatus: "pending",
  };
}

export function isBakedProductVersion(version?: { idsev?: string; idversi?: string; version?: string } | null): boolean {
  if (!version) return false;
  return version.idsev === DEFAULT_PRODUCT_VERSION_FIELDS.idsev
    && version.idversi === DEFAULT_PRODUCT_VERSION_FIELDS.idversi
    && version.version === DEFAULT_PRODUCT_VERSION_FIELDS.version;
}

export function isVersionActive(version?: { isActive?: boolean } | null): boolean {
  return version?.isActive !== false;
}

export function certificationStatusOf(version?: { certificationStatus?: string } | null): "certified" | "pending" {
  return version?.certificationStatus === "certified" ? "certified" : "pending";
}

export function isRecordLocked(version?: { lockConfirmed?: boolean } | null): boolean {
  return version?.lockConfirmed === true;
}

export function isCertifiedVersion(version?: { certificationStatus?: string } | null): boolean {
  return certificationStatusOf(version) === "certified";
}

export function parentVersionOf(version?: { version?: string; parentVersion?: string } | null): string {
  return String(version?.parentVersion || version?.version || "");
}

export function compareVersionNumbers(left: string, right: string): number {
  const a = left.split(".").map((part) => Number(part) || 0);
  const b = right.split(".").map((part) => Number(part) || 0);
  const length = Math.max(a.length, b.length);
  for (let index = 0; index < length; index += 1) {
    const diff = (a[index] || 0) - (b[index] || 0);
    if (diff) return diff;
  }
  return 0;
}

export function isVersionIdentifier(value: string): boolean {
  return /^[0-9A-Fa-f]{16}$/.test(value);
}
